import { holdoutRulesDefaults } from '../../../lib/s2/defaults';
import { NAV_AREAS, type HoldoutRulesDef } from '../../../lib/s2/types';
import { withDefaults } from '../../../lib/teamsDefaults';
import { Fieldset, NumberField } from '../fields';
import type { FormProps } from '../types';
import { KeyValueEditor } from './kv';

/** The holdout rules section (holdout.html): zombie caps, nav area costs, spill queue, stragglers and door bash. */
export function HoldoutRulesForm({ value, onChange, errors }: FormProps<HoldoutRulesDef>) {
  const d = holdoutRulesDefaults();
  const base = withDefaults(d, value ?? {}) as Required<HoldoutRulesDef>;
  const r = {
    ...base,
    liveCap: { ...d.liveCap, ...base.liveCap },
    stragglers: { ...d.stragglers, ...base.stragglers },
    areaCost: base.areaCost ?? {},
  } as Required<HoldoutRulesDef> & {
    liveCap: Required<NonNullable<HoldoutRulesDef['liveCap']>>;
    stragglers: Required<NonNullable<HoldoutRulesDef['stragglers']>>;
  };
  const set = <K extends keyof HoldoutRulesDef>(k: K, v: HoldoutRulesDef[K]) =>
    onChange({ ...r, [k]: v });
  const cap = (k: 'low' | 'mid' | 'high', label: string, hint?: string) => (
    <NumberField
      key={k}
      label={label}
      hint={hint}
      integer
      min={0}
      value={r.liveCap[k]}
      issues={errors[`liveCap.${k}`]}
      onChange={(v) => set('liveCap', { ...r.liveCap, [k]: v ?? 0 })}
    />
  );
  return (
    <>
      <Fieldset legend="Caps">
        {cap('low', 'Live cap low', 'M4: unknown devices use low.')}
        {cap('mid', 'Live cap mid')}
        {cap('high', 'Live cap high')}
        <NumberField
          label="Corpse cap"
          integer
          min={0}
          value={r.corpseCap}
          issues={errors.corpseCap}
          onChange={(v) => set('corpseCap', v ?? 0)}
        />
        <NumberField
          label="Spill queue"
          integer
          min={0}
          value={r.spillQueue}
          issues={errors.spillQueue}
          onChange={(v) => set('spillQueue', v ?? 0)}
        />
        <NumberField
          label="Door bash HP"
          integer
          min={0}
          value={r.doorBashHp}
          issues={errors.doorBashHp}
          onChange={(v) => set('doorBashHp', v ?? 0)}
        />
      </Fieldset>
      <Fieldset legend="Stragglers">
        <NumberField
          label="Stragglers count"
          integer
          min={0}
          value={r.stragglers.count}
          issues={errors['stragglers.count']}
          onChange={(v) => set('stragglers', { ...r.stragglers, count: v ?? 0 })}
        />
        <NumberField
          label="Stragglers after (s)"
          min={0}
          value={r.stragglers.afterS}
          issues={errors['stragglers.afterS']}
          onChange={(v) => set('stragglers', { ...r.stragglers, afterS: v ?? 0 })}
        />
      </Fieldset>
      <Fieldset legend="Nav areas">
        <KeyValueEditor
          label="Area cost"
          base="areaCost"
          kind="number"
          keys={NAV_AREAS}
          hint="Path cost of a placeable's nav area; higher steers zombies around it."
          value={r.areaCost}
          errors={errors}
          onChange={(v) => set('areaCost', v)}
        />
      </Fieldset>
    </>
  );
}
